import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Tooltip,
  TextField,
  InputAdornment,
  Paper,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  CircularProgress
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  History as HistoryIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
  Clear as ClearIcon,
  CheckCircleOutlined,
  WarningAmberOutlined,
  ErrorOutlined,
  Email as EmailIcon,
  ConfirmationNumber as TicketIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_ENDPOINTS } from '../../../../Api/endpoints';
import { addAuthHeader } from '../../../../utils/authUtils';

/**
 * Document status constants
 */
const documentStatuses = {
  ACTIVE: 'ACTIVE',
  EXPIRED: 'EXPIRED',
  EXPIRING_SOON: 'EXPIRING_SOON',
};

/**
 * Get status chip configuration
 */
const getStatusConfig = (status) => {
  switch (status) {
    case documentStatuses.ACTIVE:
      return { label: 'Active', color: 'success', icon: <CheckCircleOutlined fontSize="small" /> };
    case documentStatuses.EXPIRED:
      return { label: 'Expired', color: 'error', icon: <ErrorOutlined fontSize="small" /> };
    case documentStatuses.EXPIRING_SOON:
      return { label: 'Expiring Soon', color: 'warning', icon: <WarningAmberOutlined fontSize="small" /> };
    default:
      return { label: status, color: 'default', icon: null };
  }
};

/**
 * Get file icon based on type
 */
const getFileIcon = (fileType) => {
  if (fileType?.includes('pdf')) {
    return <PictureAsPdfIcon sx={{ color: '#E53935', fontSize: 20 }} />;
  }
  if (fileType?.includes('image')) {
    return <ImageIcon sx={{ color: '#43A047', fontSize: 20 }} />;
  }
  return <DescriptionIcon sx={{ color: '#1E88E5', fontSize: 20 }} />;
};

/**
 * Format date
 */
const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Document List Component
 * Displays documents in a table format with actions and pagination
 */
const DocumentList = ({ documents, onDocumentClick, onViewHistory, onRefresh }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [orderBy, setOrderBy] = useState('uploadedAt');
  const [order, setOrder] = useState('desc');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [renewalDialogOpen, setRenewalDialogOpen] = useState(false);
  const [renewalOptions, setRenewalOptions] = useState({ workflows: [], services: [] });
  const [renewalLoading, setRenewalLoading] = useState(false);

  // Handle sort
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Handle menu open
  const handleMenuOpen = (event, document) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedDoc(document);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDoc(null);
  };

  // Handle actions
  const handleView = () => {
    if (selectedDoc) {
      onDocumentClick(selectedDoc);
    }
    handleMenuClose();
  };

  const downloadSingleFile = async (filePath, fileName) => {
    const downloadUrl = API_ENDPOINTS.FILES.DOWNLOAD(filePath);
    const response = await axios.get(downloadUrl, {
      headers: addAuthHeader(),
      responseType: 'blob'
    });
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = window.document.createElement('a');
    link.href = url;
    link.download = fileName || 'document';
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDownload = async () => {
    if (!selectedDoc) { handleMenuClose(); return; }
    try {
      // Download all files in the current group
      const docs = selectedDoc.currentGroupDocs || [];
      if (docs.length > 1) {
        for (const doc of docs) {
          if (doc.filePath) {
            await downloadSingleFile(doc.filePath, doc.fileName);
          }
        }
      } else if (selectedDoc.filePath) {
        await downloadSingleFile(selectedDoc.filePath, selectedDoc.fileName);
      }
    } catch (err) {
      console.error('Download failed:', err);
    }
    handleMenuClose();
  };

  const handleViewHistory = () => {
    if (selectedDoc && onViewHistory) {
      onViewHistory(selectedDoc);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    console.log('Delete document:', selectedDoc?.id);
    handleMenuClose();
  };

  const handleSendReminder = () => {
    alert(`Reminder email will be sent for "${selectedDoc?.documentTypeName}"\n\nThis feature requires backend implementation.`);
    handleMenuClose();
  };

  const handleCreateTicket = async () => {
    const docTypeId = selectedDoc?.documentTypeId;
    handleMenuClose();
    if (!docTypeId) {
      alert('No document type associated with this document.');
      return;
    }

    try {
      setRenewalLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`/api/organization-document-types/${docTypeId}/renewal-options`, {
        headers: addAuthHeader({ 'Content-Type': 'application/json' })
      });
      const data = response.data || {};
      const workflows = data.workflows || [];
      const services = data.services || [];

      if (workflows.length === 1) {
        // Single workflow — auto-navigate
        navigate(`/CST-Workflow/new-request?workflowId=${workflows[0].id}`);
      } else if (workflows.length === 0 && services.length === 1) {
        // No workflows, single service — auto-navigate
        navigate(`/CST-DocServices/new-request?serviceId=${services[0].id}`);
      } else if (workflows.length === 0 && services.length === 0) {
        alert(`No workflows or services configured for "${selectedDoc?.documentTypeName}". Please contact your administrator.`);
      } else {
        // Multiple options — show selection dialog
        setRenewalOptions({ workflows, services });
        setRenewalDialogOpen(true);
      }
    } catch (err) {
      console.error('Error fetching renewal options:', err);
      alert('Failed to load renewal options. Please try again.');
    } finally {
      setRenewalLoading(false);
    }
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter and sort documents
  const processedDocuments = useMemo(() => {
    let filtered = documents.filter(doc =>
      doc.documentTypeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.fileName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.issuingAuthority?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    filtered = [...filtered].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      if (aValue == null) return order === 'asc' ? 1 : -1;
      if (bValue == null) return order === 'asc' ? -1 : 1;

      if (orderBy.includes('Date') || orderBy.includes('At')) {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      if (order === 'asc') {
        return aValue < bValue ? -1 : 1;
      }
      return aValue > bValue ? -1 : 1;
    });

    return filtered;
  }, [documents, searchTerm, orderBy, order]);

  // Paginate
  const paginatedDocuments = useMemo(() => {
    const start = page * rowsPerPage;
    return processedDocuments.slice(start, start + rowsPerPage);
  }, [processedDocuments, page, rowsPerPage]);

  return (
    <Box>
      {/* Search */}
      <Box sx={{ mb: 2.5 }}>
        <TextField
          size="small"
          placeholder="Search documents..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0);
          }}
          sx={{ width: 320 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        {searchTerm && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Found {processedDocuments.length} document{processedDocuments.length !== 1 ? 's' : ''} matching "{searchTerm}"
          </Typography>
        )}
      </Box>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="medium">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'documentTypeName'}
                  direction={orderBy === 'documentTypeName' ? order : 'asc'}
                  onClick={() => handleRequestSort('documentTypeName')}
                >
                  Document
                </TableSortLabel>
              </TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Authority</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'issueDate'}
                  direction={orderBy === 'issueDate' ? order : 'asc'}
                  onClick={() => handleRequestSort('issueDate')}
                >
                  Issue Date
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'expiryDate'}
                  direction={orderBy === 'expiryDate' ? order : 'asc'}
                  onClick={() => handleRequestSort('expiryDate')}
                >
                  Expiry Date
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleRequestSort('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedDocuments.map((doc) => {
              const statusConfig = getStatusConfig(doc.status);

              return (
                <TableRow
                  key={doc.id}
                  hover
                  sx={{
                    cursor: 'pointer',
                    '&:last-child td': { borderBottom: 0 }
                  }}
                  onClick={() => onDocumentClick(doc)}
                >
                  {/* Document */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1,
                          bgcolor: 'grey.100',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {getFileIcon(doc.fileType)}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight="medium" noWrap>
                          {doc.documentTypeName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                          {doc.fileCount > 1 ? `${doc.fileCount} files` : doc.fileName}
                        </Typography>
                      </Box>
                      {doc.version > 1 && (
                        <Tooltip title={`Version ${doc.version}`}>
                          <Chip
                            label={`v${doc.version}`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>

                  {/* Category */}
                  <TableCell>
                    <Chip
                      label={doc.categoryName}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.75rem', fontWeight: 500 }}
                    />
                  </TableCell>

                  {/* Authority */}
                  <TableCell>
                    <Tooltip title={doc.issuingAuthorityName || '-'}>
                      <Typography variant="body2">
                        {doc.issuingAuthorityShortName || '-'}
                      </Typography>
                    </Tooltip>
                  </TableCell>

                  {/* Issue Date */}
                  <TableCell>
                    <Typography variant="body2">{formatDate(doc.issueDate)}</Typography>
                  </TableCell>

                  {/* Expiry Date */}
                  <TableCell>
                    <Box>
                      <Typography variant="body2">{formatDate(doc.expiryDate)}</Typography>
                      {doc.daysUntilExpiry !== null && doc.daysUntilExpiry <= 30 && doc.daysUntilExpiry > 0 && (
                        <Typography variant="caption" display="block" color="warning.main" fontWeight="medium">
                          {doc.daysUntilExpiry} days left
                        </Typography>
                      )}
                      {doc.daysUntilExpiry !== null && doc.daysUntilExpiry < 0 && (
                        <Typography variant="caption" display="block" color="error.main" fontWeight="medium">
                          Expired {Math.abs(doc.daysUntilExpiry)} days ago
                        </Typography>
                      )}
                    </Box>
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <Chip
                      icon={statusConfig.icon}
                      label={statusConfig.label}
                      size="small"
                      color={statusConfig.color}
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        '& .MuiChip-icon': { fontSize: 16 }
                      }}
                    />
                  </TableCell>

                  {/* Actions */}
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, doc)}
                      sx={{
                        '&:hover': { bgcolor: 'grey.100' }
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={processedDocuments.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{ borderTop: 1, borderColor: 'divider' }}
      />

      {/* No results */}
      {processedDocuments.length === 0 && searchTerm && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <SearchIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body1" color="text.secondary">
            No documents match "{searchTerm}"
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Try adjusting your search term
          </Typography>
        </Box>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: { minWidth: 180, boxShadow: 3 }
        }}
      >
        <MenuItem onClick={handleView}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDownload}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>
        <MenuItem divider />
        <MenuItem onClick={handleSendReminder} sx={{ color: 'primary.main' }}>
          <ListItemIcon>
            <EmailIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>Send Reminder Email</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCreateTicket} sx={{ color: 'primary.main' }}>
          <ListItemIcon>
            <TicketIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>Create Renewal Ticket</ListItemText>
        </MenuItem>
        <MenuItem divider />
        <MenuItem onClick={handleViewHistory}>
          <ListItemIcon>
            <HistoryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Version History</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Renewal Options Dialog */}
      <Dialog open={renewalDialogOpen} onClose={() => setRenewalDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Select Renewal Option</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Multiple options are available for this document type. Please select one:
          </Typography>
          <List>
            {renewalOptions.workflows.map((wf) => (
              <ListItem key={wf.id} disablePadding>
                <ListItemButton onClick={() => {
                  setRenewalDialogOpen(false);
                  navigate(`/CST-Workflow/new-request?workflowId=${wf.id}`);
                }}>
                  <ListItemText
                    primary={wf.name}
                    secondary={<Chip label="Workflow" size="small" color="primary" variant="outlined" sx={{ mt: 0.5 }} />}
                  />
                </ListItemButton>
              </ListItem>
            ))}
            {renewalOptions.services.map((svc) => (
              <ListItem key={svc.id} disablePadding>
                <ListItemButton onClick={() => {
                  setRenewalDialogOpen(false);
                  navigate(`/CST-DocServices/new-request?serviceId=${svc.id}`);
                }}>
                  <ListItemText
                    primary={svc.name}
                    secondary={<Chip label="Service" size="small" color="secondary" variant="outlined" sx={{ mt: 0.5 }} />}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenewalDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Loading overlay for renewal options */}
      {renewalLoading && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.3)', zIndex: 9999 }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default DocumentList;
